/**
 * Who is signed in, for the whole app. Loaded once at start; updated on
 * sign-in, sign-out and password change, and cleared whenever the server
 * answers that the session has ended, so the app returns to the sign-in
 * screen instead of a page that still says "Signed in as".
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiFailure, api, onSessionEnded, type User } from './api.ts';

interface SessionState {
  /** undefined while loading, null when signed out. */
  user: User | null | undefined;
  setUser: (user: User | null) => void;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    try {
      const { user: me } = await api.me();
      setUser(me);
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) setUser(null);
      else throw err;
    }
  }, []);

  // Any request, from any page, that finds the session ended (signed out
  // elsewhere, password reset, account deactivated, expired).
  useEffect(
    () =>
      onSessionEnded(() => {
        setUser(null);
      }),
    [],
  );

  useEffect(() => {
    refresh().catch(() => {
      // The server could not be reached; the sign-in page will say so on the next attempt.
      setUser(null);
    });
  }, [refresh]);

  const value = useMemo(() => ({ user, setUser, refresh }), [user, refresh]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
